import React, { useState, useEffect, useCallback } from 'react';
import api from './api';
import { formatTimeAgo, formatDate } from './utils';

const ActivityFeed = ({ onBack, user, registeredRaceIds, onRegister, onViewProfile }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [cheerAnimations, setCheerAnimations] = useState(new Set());
  const [followingCount, setFollowingCount] = useState(null);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [commentsByItem, setCommentsByItem] = useState({});
  const [commentText, setCommentText] = useState({});
  const [postingComment, setPostingComment] = useState(new Set());

  // Feed improvements
  const [athleteSearch, setAthleteSearch] = useState('');
  const [athleteResults, setAthleteResults] = useState([]);
  const [, setSearching] = useState(false);
  const [upcomingRaces, setUpcomingRaces] = useState([]);
  const [raceOverlaps, setRaceOverlaps] = useState([]);

  const itemKey = (item) => `${item.event_type}:${item.event_id}`;

  const fetchFeed = useCallback(async (before = null) => {
    try {
      const params = before ? `?before=${encodeURIComponent(before)}` : '';
      const res = await api.get(`/feed${params}`);
      if (before) {
        setItems(prev => [...prev, ...res.data.items]);
      } else {
        setItems(res.data.items);
      }
      setNextCursor(res.data.nextCursor);
      setHasMore(res.data.hasMore);
    } catch (err) {
      console.error('Feed error:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const countRes = await api.get('/follows/counts');
        setFollowingCount(countRes.data.following);
      } catch (err) {
        setFollowingCount(0);
      }
      await fetchFeed();
      setLoading(false);

      // Load upcoming races & overlaps in background
      try {
        const [regRes, overlapRes] = await Promise.all([
          api.get('/registrations'),
          api.get('/follows/race-overlaps'),
        ]);
        const today = new Date().toISOString().slice(0, 10);
        setUpcomingRaces(
          (regRes.data || [])
            .filter(r => r.race && (r.race.date || '') >= today)
            .map(r => r.race)
            .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
            .slice(0, 3)
        );
        setRaceOverlaps((overlapRes.data || []).slice(0, 3));
      } catch {
        // non-critical
      }
    };
    load();
  }, [fetchFeed]);

  // Athlete search
  useEffect(() => {
    if (!athleteSearch.trim()) {
      setAthleteResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/auth/users/search?q=${encodeURIComponent(athleteSearch)}`);
        setAthleteResults(res.data.slice(0, 5));
      } catch {
        setAthleteResults([]);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [athleteSearch]);

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    await fetchFeed(nextCursor);
    setLoadingMore(false);
  };

  const handleCheer = async (item) => {
    const key = itemKey(item);
    const wasCheered = item.user_cheered || item.user_clapped;

    // Optimistic update
    setItems(prev => prev.map(i => {
      if (itemKey(i) !== key) return i;
      const count = (i.cheer_count || i.clap_count || 0);
      return {
        ...i,
        user_cheered: !wasCheered,
        user_clapped: !wasCheered,
        cheer_count: wasCheered ? count - 1 : count + 1,
        clap_count: wasCheered ? count - 1 : count + 1,
      };
    }));

    // Trigger confetti animation on cheer (not un-cheer)
    if (!wasCheered) {
      setCheerAnimations(prev => new Set([...prev, key]));
      setTimeout(() => setCheerAnimations(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      }), 800);
    }

    try {
      const res = await api.post('/feed/cheer', {
        event_type: item.event_type,
        event_id: item.event_id,
      });
      setItems(prev => prev.map(i => {
        if (itemKey(i) !== key) return i;
        return {
          ...i,
          user_cheered: res.data.cheered,
          user_clapped: res.data.cheered,
          cheer_count: res.data.cheer_count,
          clap_count: res.data.cheer_count,
        };
      }));
    } catch (err) {
      setItems(prev => prev.map(i => {
        if (itemKey(i) !== key) return i;
        return { ...i, user_cheered: item.user_cheered, user_clapped: item.user_clapped, cheer_count: item.cheer_count, clap_count: item.clap_count };
      }));
    }
  };

  const toggleComments = async (item) => {
    const key = itemKey(item);
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

    // Fetch comments if not already loaded
    if (!commentsByItem[key]) {
      try {
        const res = await api.get(`/feed/${item.event_type}/${item.event_id}/comments`);
        setCommentsByItem(prev => ({ ...prev, [key]: res.data }));
      } catch (err) {
        console.error('Fetch comments error:', err);
      }
    }
  };

  const handlePostComment = async (item) => {
    const key = itemKey(item);
    const text = (commentText[key] || '').trim();
    if (!text) return;

    setPostingComment(prev => new Set(prev).add(key));

    try {
      const res = await api.post(`/feed/${item.event_type}/${item.event_id}/comments`, {
        content: text,
      });
      setCommentsByItem(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), res.data],
      }));
      setCommentText(prev => ({ ...prev, [key]: '' }));
      // Update comment count on the item
      setItems(prev => prev.map(i => {
        if (itemKey(i) !== key) return i;
        return { ...i, comment_count: (i.comment_count || 0) + 1 };
      }));
    } catch (err) {
      console.error('Post comment error:', err);
    } finally {
      setPostingComment(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const getUserName = (item) => {
    if (item.first_name) return `${item.first_name} ${item.last_name || ''}`.trim();
    return item.user_name || 'Runner';
  };

  const getInitials = (item) => {
    if (item.first_name) return (item.first_name[0] + (item.last_name?.[0] || '')).toUpperCase();
    return (item.user_name || 'R').substring(0, 2).toUpperCase();
  };

  const getCommentAuthorName = (c) => {
    if (c.author_first_name) return `${c.author_first_name} ${c.author_last_name || ''}`.trim();
    return c.author_name || 'Runner';
  };

  const getCommentInitials = (c) => {
    if (c.author_first_name) return (c.author_first_name[0] + (c.author_last_name?.[0] || '')).toUpperCase();
    return (c.author_name || 'R').substring(0, 2).toUpperCase();
  };

  const renderStars = (rating) => {
    return (
      <span aria-label={`${rating} out of 5 stars`}>
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} aria-hidden="true" style={{ color: i < rating ? '#FF8A3D' : '#2A2A35', fontSize: '0.9rem' }}>
            {'\u2605'}
          </span>
        ))}
      </span>
    );
  };

  const renderAvatar = (item) => {
    const name = item.user_name || item.first_name || 'Runner';
    if (item.profile_photo) {
      return (
        <img
          src={item.profile_photo}
          alt={`${name} avatar`}
          style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onViewProfile(item.user_id); }}
        />
      );
    }
    return (
      <div
        aria-label={`${name} avatar`}
        style={{
          width: '40px', height: '40px', borderRadius: '50%',
          background: 'rgba(255, 138, 61, 0.2)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: '0.85rem', fontWeight: 700, color: '#FF8A3D', flexShrink: 0, cursor: 'pointer',
        }}
        onClick={(e) => { e.stopPropagation(); onViewProfile(item.user_id); }}
      >
        {getInitials(item)}
      </div>
    );
  };

  const renderCommentAvatar = (c) => {
    if (c.author_photo) {
      return (
        <img
          src={c.author_photo}
          alt={`${c.author_name || 'Runner'} avatar`}
          style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      );
    }
    return (
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%',
        background: 'rgba(255, 138, 61, 0.15)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: '0.7rem', fontWeight: 700, color: '#FF8A3D', flexShrink: 0,
      }}>
        {getCommentInitials(c)}
      </div>
    );
  };

  const renderCardFooter = (item) => {
    const key = itemKey(item);
    const cheered = item.user_cheered || item.user_clapped;
    const cheerCount = item.cheer_count || item.clap_count || 0;
    const commentCount = item.comment_count || 0;
    const isExpanded = expandedComments.has(key);
    const comments = commentsByItem[key] || [];
    const isPosting = postingComment.has(key);
    const isAnimating = cheerAnimations.has(key);

    return (
      <>
        <div style={styles.cardFooter}>
          <button
            onClick={(e) => { e.stopPropagation(); handleCheer(item); }}
            aria-label={cheered ? `Remove cheer (${cheerCount})` : `Cheer (${cheerCount})`}
            aria-pressed={cheered}
            style={{
              ...styles.footerButton,
              color: cheered ? '#FF8A3D' : '#8b9cb3',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <span style={{
              fontSize: '1rem',
              display: 'inline-block',
              transform: isAnimating ? 'scale(1.4)' : 'scale(1)',
              transition: 'transform 0.2s ease',
            }}>
              {'\uD83C\uDF89'}
            </span>
            {cheerCount > 0 && <span style={{ marginLeft: '0.3rem' }}>{cheerCount}</span>}
            {isAnimating && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '0.7rem',
                animation: 'cheerFloat 0.8s ease-out forwards',
                pointerEvents: 'none',
              }}>
                {'\uD83C\uDF89\u2728\uD83C\uDF89'}
              </span>
            )}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleComments(item); }}
            style={styles.footerButton}
          >
            <span style={{ fontSize: '1rem' }}>{'\uD83D\uDCAC'}</span>
            {commentCount > 0 && <span style={{ marginLeft: '0.3rem' }}>{commentCount}</span>}
          </button>
        </div>

        {isExpanded && (
          <div style={styles.commentsSection}>
            {comments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
                {comments.map(c => (
                  <div key={c.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                    {renderCommentAvatar(c)}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem' }}>
                        <span style={{ fontWeight: 600, color: '#FFFFFF' }}>{getCommentAuthorName(c)}</span>
                        <span style={{ color: '#5a6a7c', marginLeft: '0.5rem', fontSize: '0.75rem' }}>{formatTimeAgo(c.created_at)}</span>
                      </div>
                      <div style={{ color: '#c5cdd8', fontSize: '0.85rem', lineHeight: 1.4, marginTop: '0.15rem', wordBreak: 'break-word' }}>
                        {c.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Write a comment..."
                value={commentText[key] || ''}
                onChange={(e) => setCommentText(prev => ({ ...prev, [key]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter' && !isPosting) handlePostComment(item); }}
                style={styles.commentInput}
              />
              <button
                onClick={() => handlePostComment(item)}
                disabled={isPosting || !(commentText[key] || '').trim()}
                style={{
                  ...styles.postButton,
                  opacity: (isPosting || !(commentText[key] || '').trim()) ? 0.4 : 1,
                  cursor: (isPosting || !(commentText[key] || '').trim()) ? 'default' : 'pointer',
                }}
              >
                {isPosting ? '...' : 'Post'}
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderCard = (item) => {
    const name = getUserName(item);

    switch (item.event_type) {
      case 'registration': {
        const isRegistered = registeredRaceIds.has(item.race_id);
        return (
          <div key={`reg-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' registered for '}
                  <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span>
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            {item.race_name && (
              <div style={styles.raceInfo}>
                {item.race_image && (
                  <div style={{
                    height: '100px', borderRadius: '10px', overflow: 'hidden', marginBottom: '0.75rem',
                    backgroundImage: `url(${item.race_image})`, backgroundSize: 'cover', backgroundPosition: 'center',
                  }} />
                )}
                <div style={{ display: 'flex', gap: '1rem', color: '#8b9cb3', fontSize: '0.85rem', flexWrap: 'wrap' }}>
                  {item.race_location && <span>{'📍'} {item.race_location}</span>}
                  {item.race_date && <span>{'📅'} {formatDate(item.race_date)}</span>}
                </div>
                {!isRegistered && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRegister({ id: item.race_id, name: item.race_name, external_url: item.external_url }); }}
                    style={styles.registerButton}
                  >
                    Register too
                  </button>
                )}
              </div>
            )}
            {renderCardFooter(item)}
          </div>
        );
      }

      case 'thread':
        return (
          <div key={`thread-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' posted in '}
                  <span style={{ color: '#8b9cb3' }}>{item.community_name}</span>
                  {item.board_name && <span style={{ color: '#8b9cb3' }}>{' > '}{item.board_name}</span>}
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            <div style={styles.threadPreview}>
              <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#FFFFFF' }}>{item.thread_title}</div>
              {item.thread_content && (
                <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {item.thread_content}{item.thread_content.length >= 200 ? '...' : ''}
                </div>
              )}
            </div>
            {renderCardFooter(item)}
          </div>
        );

      case 'review':
        return (
          <div key={`review-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' reviewed '}
                  <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span>
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            <div style={styles.reviewBody}>
              <div style={{ marginBottom: '0.5rem' }}>{renderStars(item.overall_rating)}</div>
              {item.review_text && (
                <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {item.review_text}{item.review_text.length >= 200 ? '...' : ''}
                </div>
              )}
            </div>
            {renderCardFooter(item)}
          </div>
        );

      case 'result': {
        const isDnf = item.result_status === 'dnf';
        const isDns = item.result_status === 'dns';
        return (
          <div key={`result-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' logged a result for '}
                  <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span>
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            <div style={styles.resultBody}>
              {isDnf && (
                <span style={{ background: 'rgba(255, 107, 107, 0.15)', color: '#ff6b6b', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600 }}>
                  DNF
                </span>
              )}
              {isDns && (
                <span style={{ background: 'rgba(255, 193, 7, 0.15)', color: '#ffc107', padding: '0.2rem 0.6rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600 }}>
                  DNS
                </span>
              )}
              {!isDnf && !isDns && item.finish_time && (
                <span style={{ color: '#4ade80', fontSize: '1.1rem', fontWeight: 700 }}>
                  {item.finish_time}
                </span>
              )}
              {item.distance && (
                <span style={{ color: '#8b9cb3', fontSize: '0.9rem', marginLeft: '0.75rem' }}>
                  {item.distance} {item.distance_unit || 'mi'}
                </span>
              )}
            </div>
            {renderCardFooter(item)}
          </div>
        );
      }

      case 'story':
        return (
          <div key={`story-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' shared a race story'}
                  {item.race_name && (
                    <span>{' about '}<span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span></span>
                  )}
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            <div style={styles.threadPreview}>
              {item.story_title && (
                <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: '#FFFFFF' }}>{item.story_title}</div>
              )}
              {item.story_content && (
                <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  {item.story_content}{item.story_content.length >= 300 ? '...' : ''}
                </div>
              )}
              {item.story_photo_url && (
                <div style={{ borderRadius: '10px', overflow: 'hidden', marginTop: '0.75rem' }}>
                  <img src={item.story_photo_url} alt="Race story" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', display: 'block' }} onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
            </div>
            {renderCardFooter(item)}
          </div>
        );

      case 'strategy':
        return (
          <div key={`strategy-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' set a goal for '}
                  <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span>
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            {item.thread_content && (
              <div style={{ ...styles.threadPreview, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.1rem' }}>{'\uD83C\uDFAF'}</span>
                <span style={{ color: '#4ade80', fontSize: '1rem', fontWeight: 700 }}>{item.thread_content}</span>
              </div>
            )}
            {renderCardFooter(item)}
          </div>
        );

      case 'reflection':
        return (
          <div key={`reflection-${item.event_id}`} style={styles.card}>
            <div style={styles.cardHeader}>
              {renderAvatar(item)}
              <div style={{ flex: 1 }}>
                <div style={styles.headline}>
                  <span style={styles.nameLink} onClick={() => onViewProfile(item.user_id)}>{name}</span>
                  {' reflected on '}
                  <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{item.race_name}</span>
                </div>
                <div style={styles.meta}>{formatTimeAgo(item.created_at)}</div>
              </div>
            </div>
            <div style={styles.reviewBody}>
              {item.overall_rating && (
                <div style={{ marginBottom: '0.5rem' }}>{renderStars(item.overall_rating)}</div>
              )}
              {item.thread_content && (
                <div style={{ color: '#8b9cb3', fontSize: '0.9rem', lineHeight: 1.5, fontStyle: 'italic' }}>
                  {'\u201C'}{item.thread_content}{item.thread_content.length >= 200 ? '...' : ''}{'\u201D'}
                </div>
              )}
            </div>
            {renderCardFooter(item)}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
      <style>{`
        @keyframes cheerFloat {
          0% { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-20px) scale(1.3); }
        }
      `}</style>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer', padding: '0.25rem' }}>
          {'←'}
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Activity Feed</h1>
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '1.5rem' }}>
        {/* Athlete Search */}
        <div style={{ marginBottom: '1rem', position: 'relative' }}>
          <input
            type="text"
            placeholder="Search athletes to follow..."
            value={athleteSearch}
            onChange={(e) => setAthleteSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.7rem 1rem 0.7rem 2.5rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.15)',
              borderRadius: '25px',
              color: '#FFFFFF',
              fontSize: '0.9rem',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <span style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', fontSize: '1rem', color: '#8b9cb3' }}>
            {'\uD83D\uDD0D'}
          </span>
          {athleteResults.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0.25rem',
              background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.2)',
              borderRadius: '12px', zIndex: 50, overflow: 'hidden',
            }}>
              {athleteResults.map(a => {
                const aName = a.first_name ? `${a.first_name} ${a.last_name || ''}`.trim() : a.name;
                return (
                  <button
                    key={a.id}
                    onClick={() => { onViewProfile(a.id); setAthleteSearch(''); setAthleteResults([]); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.65rem', width: '100%',
                      padding: '0.65rem 1rem', background: 'transparent', border: 'none',
                      borderBottom: '1px solid rgba(255, 138, 61, 0.06)', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    {a.profile_photo ? (
                      <img src={a.profile_photo} alt={`${a.name || 'Athlete'} avatar`} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%',
                        background: 'rgba(255, 138, 61, 0.2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 700, color: '#FF8A3D',
                      }}>
                        {(aName || 'R')[0].toUpperCase()}
                      </div>
                    )}
                    <span style={{ color: '#FFFFFF', fontSize: '0.9rem', fontWeight: 500 }}>{aName}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming Races */}
        {upcomingRaces.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ color: '#8b9cb3', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 0.6rem 0' }}>
              Your Upcoming Races
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.25rem' }}>
              {upcomingRaces.map(race => {
                const daysLeft = Math.ceil((new Date(race.date) - new Date()) / 86400000);
                return (
                  <div key={race.id} style={{
                    background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.12)',
                    borderRadius: '12px', padding: '0.65rem 0.85rem', minWidth: '160px', flexShrink: 0,
                  }}>
                    <div style={{ color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3, marginBottom: '0.25rem' }}>
                      {race.name}
                    </div>
                    <div style={{ color: daysLeft <= 7 ? '#FF8A3D' : '#8b9cb3', fontSize: '0.75rem', fontWeight: 600 }}>
                      {daysLeft <= 0 ? 'Today!' : `${daysLeft}d away`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Friends Running With You */}
        {raceOverlaps.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ color: '#8b9cb3', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 0.6rem 0' }}>
              Friends Running With You
            </h3>
            {raceOverlaps.map(item => (
              <div key={item.race.id} style={{
                background: '#16161D', border: '1px solid rgba(255, 138, 61, 0.12)',
                borderRadius: '12px', padding: '0.65rem 0.85rem', marginBottom: '0.4rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ color: '#FFFFFF', fontSize: '0.85rem', fontWeight: 600 }}>{item.race.name}</div>
                  <div style={{ color: '#8b9cb3', fontSize: '0.75rem' }}>{formatDate(item.race.date)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  {item.runners.slice(0, 3).map(r => (
                    r.profile_photo ? (
                      <img key={r.id} src={r.profile_photo} alt={`${r.name || 'Runner'} avatar`} style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #16161D' }} />
                    ) : (
                      <div key={r.id} style={{
                        width: '24px', height: '24px', borderRadius: '50%',
                        background: 'rgba(255, 138, 61, 0.2)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.6rem', fontWeight: 700, color: '#FF8A3D',
                        border: '1px solid #16161D',
                      }}>
                        {((r.first_name || r.name || 'R')[0]).toUpperCase()}
                      </div>
                    )
                  ))}
                  {item.runners.length > 3 && (
                    <span style={{ color: '#8b9cb3', fontSize: '0.75rem', marginLeft: '0.25rem' }}>+{item.runners.length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <p role="status" aria-live="polite" style={{ color: '#8b9cb3', textAlign: 'center', padding: '3rem' }}>Loading feed...</p>
        ) : followingCount === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{'👋'}</div>
            <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>Follow some athletes</h3>
            <p style={{ color: '#8b9cb3', lineHeight: 1.6 }}>
              Use the search bar above to find athletes and follow them!
            </p>
          </div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{'📭'}</div>
            <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No recent activity</h3>
            <p style={{ color: '#8b9cb3', lineHeight: 1.6 }}>
              No recent activity from athletes you follow.
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {items.map(renderCard)}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    padding: '0.75rem 2rem',
                    background: 'transparent',
                    border: '1px solid rgba(255, 138, 61, 0.3)',
                    color: '#FF8A3D',
                    borderRadius: '25px',
                    cursor: loadingMore ? 'default' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  card: {
    background: '#16161D',
    border: '1px solid rgba(255, 138, 61, 0.1)',
    borderRadius: '16px',
    padding: '1.25rem',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  headline: {
    fontSize: '0.95rem',
    lineHeight: 1.4,
    color: '#FFFFFF',
  },
  nameLink: {
    fontWeight: 600,
    cursor: 'pointer',
    color: '#FFFFFF',
  },
  meta: {
    color: '#8b9cb3',
    fontSize: '0.8rem',
    marginTop: '0.2rem',
  },
  raceInfo: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.08)',
  },
  registerButton: {
    marginTop: '0.75rem',
    padding: '0.45rem 1.25rem',
    background: 'transparent',
    border: '1px solid #FF8A3D',
    color: '#FF8A3D',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  threadPreview: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.08)',
  },
  reviewBody: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.08)',
  },
  resultBody: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.08)',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1.5rem',
  },
  cardFooter: {
    marginTop: '0.75rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.06)',
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  footerButton: {
    background: 'transparent',
    border: 'none',
    color: '#8b9cb3',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
    fontWeight: 600,
    padding: '0.25rem 0.5rem',
    borderRadius: '8px',
  },
  commentsSection: {
    marginTop: '0.6rem',
    paddingTop: '0.6rem',
    borderTop: '1px solid rgba(255, 138, 61, 0.06)',
  },
  commentInput: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255, 138, 61, 0.15)',
    borderRadius: '20px',
    padding: '0.45rem 0.85rem',
    color: '#FFFFFF',
    fontSize: '0.85rem',
    outline: 'none',
  },
  postButton: {
    background: 'transparent',
    border: '1px solid #FF8A3D',
    color: '#FF8A3D',
    borderRadius: '16px',
    padding: '0.4rem 0.9rem',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
};

export default ActivityFeed;
